import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["__scopePromoModal"],
  _excluded2 = ["children"],
  _excluded3 = ["className", "children"],
  _excluded4 = ["__scopePromoModal", "asChild"],
  _excluded5 = ["__scopePromoModal", "asChild"],
  _excluded6 = ["size"],
  _excluded7 = ["__scopePromoModal"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import { clsx } from 'clsx';
import { Button } from '../../../button';
import { createDialogScope } from '../../../headless/dialog/context';
import { DomPrimitive } from '../../../internal/components/DomPrimitive';
import { Slot } from '../../../internal/components/Slot';
import { createContextScope } from '../../../internal/context/scoped-context';
import { useComposedRefs } from '../../../internal/hooks/useComposedRefs';
import { composeEventHandlers } from '../../../internal/utils/composeEventHandlers';
import { ModalClose, ModalCloseButton, ModalContent, ModalDescription, ModalFooter, ModalRoot, ModalTitle, ModalTrigger } from '../Modal';
import * as promoModalStyles from './PromoModal.module.css';
import { jsx as _jsx } from "react/jsx-runtime";
const ROOT_NAME = 'PromoModal';
const BODY_NAME = 'PromoModalBody';
const CONTENT_NAME = 'PromoModalContent';
const ACTIONS_NAME = 'PromoModalActions';
const ACTION_NAME = 'PromoModalAction';
const ACTION_SECONDARY_NAME = 'PromoModalCancel';
const MEDIA_NAME = 'PromoModalMedia';

/**
 * создаем отдельный скоуп для PromoModal
 * чтобы избежать ошибок композиции контекстов с другими модальными окнами
 */

const [createPromoModalContext, createPromoModalScope] = createContextScope(ROOT_NAME, [createDialogScope]);
const useDialogScope = createDialogScope();
const [PromoModalContentProvider, usePromoModalContentContext] = createPromoModalContext(CONTENT_NAME);

/**
 * HOC для оборачивания компонентов Modal в скоуп PromoModal
 * при необходимости расширения функциональности, можно создать отдельные компонеты, см PromoModalContent
 */
const withPromoModalScope = Component => {
  const WrappedComponent = props => {
    const {
        __scopePromoModal
      } = props,
      restProps = _objectWithoutProperties(props, _excluded);
    const ModalScope = useDialogScope(__scopePromoModal);
    return /*#__PURE__*/_jsx(Component, _objectSpread(_objectSpread({}, ModalScope), restProps));
  };
  WrappedComponent.displayName = "Promo(".concat(Component.displayName || Component.name, ")");
  return WrappedComponent;
};

/**
 * Копоненты Modal без изменения функционала, обернутые в скоуп PromoModal
 */
export const PromoModalRoot = withPromoModalScope(ModalRoot);
export const PromoModalTrigger = withPromoModalScope(ModalTrigger);
export const PromoModalTitle = withPromoModalScope(ModalTitle);
export const PromoModalDescription = withPromoModalScope(ModalDescription);
export const PromoModalClose = withPromoModalScope(ModalClose);
export const PromoModalCloseButton = withPromoModalScope(ModalCloseButton);
export const PromoModalFooter = withPromoModalScope(ModalFooter);

/**
 * Компонент для рендера тела модального окна
 */

export const PromoModalBody = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      children
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded2);
  return /*#__PURE__*/_jsx(DomPrimitive.div, _objectSpread(_objectSpread({
    className: promoModalStyles['body']
  }, rest), {}, {
    ref: ref,
    children: children
  }));
});
PromoModalBody.displayName = BODY_NAME;

/**
 * Компонент для рендера контейнера медиа-изображения в заголовке модального окна
 */

export const PromoModalMedia = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
  let {
      className,
      children
    } = _ref2,
    rest = _objectWithoutProperties(_ref2, _excluded3);
  return /*#__PURE__*/_jsx(DomPrimitive.div, _objectSpread(_objectSpread({
    className: clsx(promoModalStyles['media'], className)
  }, rest), {}, {
    ref: ref,
    children: children
  }));
});
PromoModalMedia.displayName = MEDIA_NAME;

/**
 * Компонент обертка PromoModalActions
 * используется для правильного позиционирования кнопок действия
 * @example
 * <PromoModalActions>
 *  <PromoModalCancel>Отмена</PromoModalCancel>
 *  <PromoModalAction>Принять</PromoModalAction>
 * </PromoModalActions>
 */

export const PromoModalActions = /*#__PURE__*/React.forwardRef((_ref3, forwardedRef) => {
  let {
    children
  } = _ref3;
  return /*#__PURE__*/_jsx(DomPrimitive.div, {
    ref: forwardedRef,
    className: promoModalStyles['actions'],
    children: children
  });
});
PromoModalActions.displayName = ACTIONS_NAME;

/**
 * Button-like компонент для основного действия в модальном окне
 */

export const PromoModalAction = /*#__PURE__*/React.forwardRef((props, forwardedRef) => {
  const {
      __scopePromoModal,
      asChild
    } = props,
    rest = _objectWithoutProperties(props, _excluded4);
  const {
    actionRef
  } = usePromoModalContentContext(ACTION_SECONDARY_NAME, __scopePromoModal);
  const ModalScope = useDialogScope(__scopePromoModal);
  const ref = useComposedRefs(forwardedRef, actionRef);
  const Comp = asChild ? Slot : Button;
  return /*#__PURE__*/_jsx(ModalClose, _objectSpread(_objectSpread({
    asChild: true
  }, ModalScope), {}, {
    children: /*#__PURE__*/_jsx(Comp, _objectSpread(_objectSpread({
      size: "L"
    }, rest), {}, {
      ref: ref
    }))
  }));
});
PromoModalAction.displayName = ACTION_NAME;

/**
 * Button-like компонент для доп действия в модальном окне
 */

export const PromoModalActionSecondary = /*#__PURE__*/React.forwardRef((props, forwardedRef) => {
  const {
      __scopePromoModal,
      asChild
    } = props,
    rest = _objectWithoutProperties(props, _excluded5);
  const ModalScope = useDialogScope(__scopePromoModal);
  const Comp = asChild ? Slot : Button;
  return /*#__PURE__*/_jsx(ModalClose, _objectSpread(_objectSpread({
    asChild: true
  }, ModalScope), {}, {
    children: /*#__PURE__*/_jsx(Comp, _objectSpread(_objectSpread({
      size: "L",
      theme: "fill_secondary"
    }, rest), {}, {
      ref: forwardedRef
    }))
  }));
});
PromoModalActionSecondary.displayName = ACTION_SECONDARY_NAME;
export const PromoModalContent = /*#__PURE__*/React.forwardRef((_ref4, forwardedRef) => {
  let {
      size
    } = _ref4,
    props = _objectWithoutProperties(_ref4, _excluded6);
  const {
      __scopePromoModal
    } = props,
    contentProps = _objectWithoutProperties(props, _excluded7);
  const modalScope = useDialogScope(__scopePromoModal);
  const actionRef = React.useRef(null);
  /**
   * При открытии фокусируемся на кнопке основного действия по умолчанию
   */
  const handleOnOpenAutoFocus = React.useMemo(() => composeEventHandlers(contentProps.onOpenAutoFocus, event => {
    var _actionRef$current;
    event.preventDefault();
    (_actionRef$current = actionRef.current) === null || _actionRef$current === void 0 || _actionRef$current.focus({
      preventScroll: true
    });
  }), [contentProps.onOpenAutoFocus]);
  return /*#__PURE__*/_jsx(PromoModalContentProvider, {
    actionRef: actionRef,
    scope: __scopePromoModal,
    children: /*#__PURE__*/_jsx(ModalContent, _objectSpread(_objectSpread({
      size: size,
      portalProps: modalScope,
      overlayProps: modalScope,
      onOpenAutoFocus: handleOnOpenAutoFocus,
      ref: forwardedRef
    }, modalScope), contentProps))
  });
});
PromoModalContent.displayName = CONTENT_NAME;
export { createPromoModalScope };